import React, { FunctionComponent, ComponentProps, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { prepareProps } from 'record-it-components/utils/propUtils';
import {
  BufferedNumberInput,
  NumberInputProps
} from 'record-it-components/input/NumberInput/NumberInput';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { BufferedTextInput } from 'record-it-components/input/TextInput/TextInput';
import { TimeTrackingConfiguration } from '../../../classes/entities/TimeTrackingConfiguration/timeTrackingConfigurationTypes';
import {
  AnyPropertyChanged,
  useEntityPropertyChangedCallback
} from '../../../hooks/entity/useEntityPropertyChangedCallback';

export type EditTimeTrackingConfigurationFormProps = ComponentProps<'div'> & {
  timeTrackingConfiguration: TimeTrackingConfiguration;
  onPropertyChanged: AnyPropertyChanged<TimeTrackingConfiguration>;
};

export const EditTimeTrackingConfigurationForm: FunctionComponent<EditTimeTrackingConfigurationFormProps> =
  React.memo((props) => {
    const { timeTrackingConfiguration, onPropertyChanged, ...remainingProps } =
      prepareProps({ props, ignoreChildren: true });
    const { t } = useTranslation();

    const maxWorkHoursPerDayChanged = useEntityPropertyChangedCallback(
      timeTrackingConfiguration,
      'maxWorkHoursPerDay',
      onPropertyChanged
    );
    const handleMaxWorkHoursPerDayInput: NumberInputProps['onInput'] =
      useCallback(
        (value) => {
          maxWorkHoursPerDayChanged(value ?? 0);
        },
        [maxWorkHoursPerDayChanged]
      );

    const handleNameInput = useEntityPropertyChangedCallback(
      timeTrackingConfiguration,
      'name',
      onPropertyChanged
    );

    return (
      <div {...remainingProps}>
        <FormGroup>
          <BufferedTextInput
            value={timeTrackingConfiguration.name}
            label={t('entities.TimeTrackingConfiguration.name')}
            onInput={handleNameInput}
          ></BufferedTextInput>
        </FormGroup>
        <FormGroup>
          <BufferedNumberInput
            value={timeTrackingConfiguration.maxWorkHoursPerDay}
            label={t('entities.TimeTrackingConfiguration.maxWorkHoursPerDay')}
            onInput={handleMaxWorkHoursPerDayInput}
            data-testid="EditTimeTrackingConfigurationForm.MaxWorkHoursPerDayInput"
          ></BufferedNumberInput>
        </FormGroup>
      </div>
    );
  });
